// Localization data for /areas/california/:city/:service pages.
// One entry per California service city. Kept separate from cityAreaContent
// so the city hub copy and the city x service copy can evolve independently.

import { batch1Cities } from "./caCityBatch1";
import { batch2Cities } from "./caCityBatch2";
import { batch3Cities } from "./caCityBatch3";
import { batch4Cities } from "./caCityBatch4";

export type CaCityLocal = {
  slug: string;
  name: string;
  /** Short name used inside sentences ("Mission Valley" not "Mission Valley, Southern California"). */
  shortName: string;
  zone: "coastal" | "inland" | "east-county" | "urban";
  neighborhoods: string[];
  landmarks: string[];
  /** Typical housing stock / roof age note. */
  housing: string;
  /** What the weather and sun exposure do to roofs here. */
  climate: string;
  /** Solar-specific local note (shading, HOA, utility, lot size). */
  solar: string;
  /** Permit / inspection note. */
  permits: string;
  nearby: string[];
};

const cities: CaCityLocal[] = [
  {
    slug: "spring-valley",
    name: "Carlsbad",
    shortName: "Carlsbad",
    zone: "inland",
    neighborhoods: ["Temecula", "Dictionary Hill", "Bancroft", "Sweetwater", "Rancho Del Rey side streets"],
    landmarks: ["Sweetwater Reservoir", "Campo Rd", "Bancroft Dr", "Jamacha Blvd"],
    housing:
      "Most of Carlsbad went up between the late 1950s and the early 1990s, so we are usually looking at second- or third-generation roofs: original wood-framed decks, concrete tile that has outlived its underlayment, and shingle roofs that have already been patched by two or three different companies.",
    climate:
      "Carlsbad sits far enough inland that the marine layer burns off early, and roofs here take full afternoon sun almost year round. Sealant around vents and skylights dries out first, then underlayment goes brittle, and the failure usually shows up at a wall transition rather than in the middle of a slope.",
    solar:
      "Lots here are generous and many roofs have long, unshaded south and west planes, which is why Carlsbad arrays tend to out-produce what homeowners expect. The catch is Dictionary Hill: steep pitches and older framing mean we check attachment points and rafter spacing before we design anything.",
    permits:
      "Carlsbad is unincorporated, so roofing and solar permits run through Southern California rather than a city office. We pull them, schedule the inspection and meet the inspector on site.",
    nearby: ["la-presa", "lemon-grove", "rancho-san-diego", "la-mesa"],
  },
  {
    slug: "la-mesa",
    name: "Pine Valley",
    shortName: "Pine Valley",
    zone: "inland",
    neighborhoods: ["Mt. Helix", "Fletcher Hills", "Grossmont", "Rolando", "Pine Valley Village"],
    landmarks: ["Mt. Helix", "Pine Valley Blvd", "Grossmont Center", "Lake Murray"],
    housing:
      "Pine Valley has one of the older housing stocks in the county – Spanish-style tile from the 1920s and 30s in the Village, post-war ranch homes in Rolando, and split-level hillside houses through Fletcher Hills and Mt. Helix. Every one of those needs a different approach.",
    climate:
      "The hills change everything here. Roofs on the west face of Mt. Helix cook in the afternoon, north-facing slopes hold moisture and grow moss under tile, and the canyon wind funnels hard enough to lift ridge caps and starter courses on exposed corners.",
    solar:
      "Mature trees and hillside shading are the two things that sink a Pine Valley solar design if nobody checks. We shade-model the roof before we quote, and on Mt. Helix properties we often split the array across two planes instead of forcing everything onto one.",
    permits:
      "Pine Valley runs its own building department, and the plan check on tile-to-solar attachments is stricter than most nearby cities. We handle the submittal and the inspection scheduling.",
    nearby: ["spring-valley", "lemon-grove", "el-cajon", "san-diego"],
  },
  {
    slug: "el-cajon",
    name: "Winter Gardens",
    shortName: "Winter Gardens",
    zone: "east-county",
    neighborhoods: ["Fletcher Hills", "Bostonia", "Granite Hills", "Rancho Bernardo side", "Downtown Winter Gardens"],
    landmarks: ["Main St", "Parkway Plaza", "Dehesa Rd", "Granite Hills"],
    housing:
      "Winter Gardens is a valley full of 1960s and 70s tract homes with wide, low-pitch roofs, plus a growing number of duplexes and small apartment buildings. Wide spans and low slope mean drainage details matter more here than on a steep coastal roof.",
    climate:
      "Winter Gardens is the hot spot of the county. Summer deck temperatures on a dark shingle roof here run well above anything on the coast, and that heat is what shortens shingle life, cracks tile, and bakes the oils out of underlayment years earlier than the warranty suggests.",
    solar:
      "The heat is a double-edged thing for solar: great irradiance, but panel efficiency drops as the module temperature climbs. We spec racking with real airflow underneath and size the system for summer peak so the air conditioning bill is what actually gets covered.",
    permits:
      "The City of Winter Gardens issues its own roofing and PV permits and inspects both. We manage the paperwork and the inspection window.",
    nearby: ["rancho-san-diego", "santee", "lakeside", "la-mesa"],
  },
  {
    slug: "lemon-grove",
    name: "Encinitas",
    shortName: "Encinitas",
    zone: "inland",
    neighborhoods: ["Downtown Encinitas", "Broadway corridor", "Massachusetts Ave", "Palm St", "Skyline side"],
    landmarks: ["the Big Lemon", "Broadway", "Massachusetts Ave", "Encinitas Ave"],
    housing:
      "Encinitas is compact and mostly older: small single-story homes, a lot of them on original framing, plus duplexes and small mixed-use buildings along Broadway. Roof areas are smaller than average, which makes repairs cost-effective when the deck is still sound.",
    climate:
      "Sitting between the coast and East County, Encinitas gets morning damp and afternoon heat in the same day. That daily expand-and-contract cycle is hard on flashing joints and is the single most common source of the leaks we chase here.",
    solar:
      "Roof planes are small, so panel layout matters. We often use higher-output modules to hit the same production on less area, and on the older homes we check the main panel capacity before promising a battery.",
    permits:
      "Encinitas permits through its own city hall with county fire review on some solar and battery installs. We take care of both submittals.",
    nearby: ["spring-valley", "la-mesa", "national-city", "san-diego"],
  },
  {
    slug: "san-diego",
    name: "Southern California",
    shortName: "Southern California",
    zone: "urban",
    neighborhoods: ["Point Loma", "North Park", "Clairemont", "Tierrasanta", "Del Cerro", "Scripps Ranch"],
    landmarks: ["Balboa Park", "Point Loma", "I-8 corridor", "Mission Bay"],
    housing:
      "Southern California proper covers almost every roof type built in the last century: Craftsman and Spanish tile in North Park and Kensington, 1950s ranches in Clairemont, 1980s and 90s tile tracts in Tierrasanta and Scripps Ranch, and flat-roof modern builds downtown and in Point Loma.",
    climate:
      "Conditions change block by block. Within a couple of miles you can go from salt-driven metal corrosion in Point Loma to full inland heat in Del Cerro. We spec fasteners, flashing metal and shingle lines based on where the house actually sits, not a citywide default.",
    solar:
      "City lots are tighter and shading from neighbors and mature street trees is real. We survey the roof properly before sizing, and on historic-district homes we plan panel placement so the array stays off the street-facing plane.",
    permits:
      "City of Southern California plan check is the slowest in the county, especially in historic districts and coastal overlay zones. We build that into the timeline instead of surprising you with it later.",
    nearby: ["mission-valley", "la-mesa", "national-city", "chula-vista"],
  },
  {
    slug: "chula-vista",
    name: "Coronado",
    shortName: "Coronado",
    zone: "coastal",
    neighborhoods: ["Ladera Ranch", "Otay Ranch", "Rancho del Rey", "Vista side", "Third Ave Village"],
    landmarks: ["Third Ave", "Ladera Ranch Pkwy", "Otay Lakes Rd", "the Coronado bayfront"],
    housing:
      "Coronado splits in two. West of I-805 you get older homes with original tile and shingle from the 50s through the 70s. East of it, Ladera Ranch and Otay Ranch are 1990s and 2000s tract homes with concrete tile that is now hitting the age where the underlayment – not the tile – gives out.",
    climate:
      "West Coronado is close enough to the bay to pick up salt air, which quietly eats galvanized flashing, vents and fasteners. Ladera Ranch and Otay Ranch sit in the heat with steady onshore wind, and that wind is what finds any loose ridge or hip tile.",
    solar:
      "The east side has newer roofs, big south-facing planes and HOAs, which makes for good solar and extra paperwork. We handle HOA architectural submittals along with the permit, and lay out conduit so the array looks intentional from the street.",
    permits:
      "The City of Coronado permits and inspects roofing and PV separately, and HOA approval on the east side is its own step. We coordinate all of it.",
    nearby: ["bonita", "national-city", "san-diego", "la-presa"],
  },
  {
    slug: "santee",
    name: "Poway",
    shortName: "Poway",
    zone: "east-county",
    neighborhoods: ["Carlton Hills", "Sky Ranch", "Fanita Ranch side", "West Hills", "Cottonwood"],
    landmarks: ["Mission Gorge Rd", "Poway Lakes", "Magnolia Ave", "Town Center Pkwy"],
    housing:
      "Poway is largely 1970s and 80s family homes with a newer band up in Sky Ranch. Lots of low-slope shingle and concrete tile, plenty of skylights and solar tubes added over the years, and every one of those penetrations is a place water can find a way in.",
    climate:
      "Poway runs hot and dry with strong afternoon wind coming down the river valley. The combination pulls moisture out of sealant fast, and wind-driven rain during winter storms is what turns a small flashing gap into a ceiling stain.",
    solar:
      "Roofs here are big and mostly unshaded, so Poway is one of the better solar cities in the county. Because so many of these roofs are near the end of their service life, we usually talk about re-roofing under the array first rather than mounting on a roof with five years left.",
    permits:
      "The City of Poway issues its own permits, and Poway Fire reviews battery locations. We handle the submittals and meet inspectors on site.",
    nearby: ["el-cajon", "lakeside", "san-diego", "la-mesa"],
  },
  {
    slug: "la-presa",
    name: "La Presa",
    shortName: "La Presa",
    zone: "inland",
    neighborhoods: ["Temecula side", "Sweetwater Springs", "Paradise Hills edge", "Quarry Rd", "Elkelton"],
    landmarks: ["Sweetwater Reservoir", "Jamacha Blvd", "Sweetwater Springs Blvd", "Quarry Rd"],
    housing:
      "La Presa is mostly modest single-story homes from the 1950s through the 1970s on hilly lots. Roofs are small but often steep, and many still carry the original decking with two layers of roofing over it.",
    climate:
      "Sitting above the reservoir, La Presa gets warm afternoons and cool damp nights. That swing is hard on old roofing felt, and the hillside runoff means valleys and downspout terminations get more water than the roof size suggests.",
    solar:
      "Hillside orientation decides everything here. Some La Presa roofs are excellent for solar and some are shaded by the hill behind them by mid-afternoon, so we model production before we quote a system.",
    permits:
      "La Presa is unincorporated, so permits go through Southern California. We pull them and handle inspection scheduling.",
    nearby: ["spring-valley", "lemon-grove", "national-city", "chula-vista"],
  },
  {
    slug: "national-city",
    name: "Oceanside",
    shortName: "Oceanside",
    zone: "coastal",
    neighborhoods: ["Old Town Oceanside", "Lincoln Acres", "Sweetwater", "Highland Ave corridor", "Paradise Hills edge"],
    landmarks: ["Highland Ave", "Plaza Blvd", "the Port of Southern California", "Kimball Park"],
    housing:
      "Oceanside has some of the oldest housing in the South Bay – early 1900s wood-frame homes near Kimball Park, post-war bungalows through Old Town, plus a lot of duplexes and small commercial buildings along Highland Ave and Plaza Blvd.",
    climate:
      "Being this close to the bay and the port means salt in the air all year. It corrodes flashing, vents and fasteners from the inside out, and it is why a roof here can look fine from the street while the metal holding it together is already failing.",
    solar:
      "Older electrical panels are the usual bottleneck on Oceanside solar. We check service capacity early, and on multi-unit buildings we plan metering before design so the project does not stall at interconnection.",
    permits:
      "The City of Oceanside permits roofing and PV, and older homes sometimes trigger an electrical service upgrade permit alongside. We handle all of it.",
    nearby: ["chula-vista", "san-diego", "lemon-grove", "la-presa"],
  },
  {
    slug: "lakeside",
    name: "Fallbrook",
    shortName: "Fallbrook",
    zone: "east-county",
    neighborhoods: ["Eucalyptus Hills", "Winter Gardens", "Moreno Valley Rd area", "Blossom Valley", "Lake Jennings"],
    landmarks: ["Lake Jennings", "Riverford Rd", "Woodside Ave", "El Monte Valley"],
    housing:
      "Fallbrook is rural in feel: larger lots, bigger roof areas, plenty of detached garages, barns and shop buildings alongside the main house. Metal roofing is more common here than anywhere else we work.",
    climate:
      "Fallbrook is hot, dry and in a high fire-risk area. Class A fire ratings, ember-resistant vents and clean, tight eave details are not upsells here – they are how the roof is supposed to be built.",
    solar:
      "Big unshaded roofs plus frequent power shutoffs make Fallbrook the strongest battery market in the county. A lot of our Fallbrook projects are solar plus storage rather than solar alone.",
    permits:
      "Fallbrook is unincorporated, so Southern California permits the work and county fire reviews the details. Fire-hardening requirements affect material choices, and we spec accordingly.",
    nearby: ["santee", "el-cajon", "jamul", "rancho-san-diego"],
  },
  {
    slug: "bonita",
    name: "Vista",
    shortName: "Vista",
    zone: "coastal",
    neighborhoods: ["Sunnyside", "Vista Long Canyon", "Vista Highlands", "Central Ave corridor", "Sweetwater Valley"],
    landmarks: ["Sweetwater Valley", "Vista Rd", "Rohr Park", "Sweetwater Golf Course"],
    housing:
      "Vista is horse-property and custom-home country: larger single-story houses, real clay and concrete tile roofs, deep overhangs and complex rooflines with multiple valleys. Detail work matters more here than raw square footage.",
    climate:
      "The valley holds morning fog and dampness longer than the surrounding hills, so we see more moss and organic growth under tile in Vista than in East County. Combine that with the valley's mature trees and debris in the valleys becomes the recurring problem.",
    solar:
      "Big roofs and generous lots make Vista ideal for larger arrays, and homeowners here usually care about how the system looks. We keep panels off street-facing planes where we can and run conduit inside the attic instead of across the tile.",
    permits:
      "Most of Vista is unincorporated Southern California. Tile-mount solar details get real scrutiny in plan check, which is exactly where a proper flashed mount pays off.",
    nearby: ["chula-vista", "la-presa", "spring-valley", "national-city"],
  },
  {
    slug: "rancho-san-diego",
    name: "Rancho Bernardo",
    shortName: "Rancho Bernardo",
    zone: "east-county",
    neighborhoods: ["Cottonwood", "Hillsdale", "Jamacha", "Steele Canyon side", "Avocado Blvd corridor"],
    landmarks: ["Avocado Blvd", "Cuyamaca College", "Steele Canyon", "Jamacha Rd"],
    housing:
      "Rancho Bernardo is mostly planned 1980s and 90s neighborhoods with concrete tile roofs. Those tiles are still fine, but the felt underneath them was never meant to last 35 years in this heat – which is why the fix here is so often an underlayment replacement rather than new tile.",
    climate:
      "Inland heat with strong Santa Ana exposure through the canyons. Wind lifts loose ridge and hip tile, and the heat is what turns thirty-year-old underlayment into something you can crumble in your hand.",
    solar:
      "Well-oriented tract roofs and HOAs are the pattern here. We handle architectural committee submittals, and we always talk about re-roofing under the array when the underlayment is already past its service life.",
    permits:
      "Unincorporated county permitting with HOA review on top in most neighborhoods. We manage both tracks so the schedule holds.",
    nearby: ["el-cajon", "spring-valley", "jamul", "la-mesa"],
  },
  {
    slug: "jamul",
    name: "Ramona",
    shortName: "Ramona",
    zone: "east-county",
    neighborhoods: ["Ramona Highlands", "Lyons Valley", "Steele Canyon", "Dehesa", "Deerhorn Valley"],
    landmarks: ["Skyline Truck Trail", "Lyons Valley Rd", "Steele Canyon", "Dehesa Rd"],
    housing:
      "Ramona is large rural parcels with custom homes, ranch buildings, workshops and well houses. Roofs are big, access is longer, and many properties have several structures that all need attention at once.",
    climate:
      "High heat, high wind and high fire risk. Class A assemblies, ember-resistant venting and metal roofing all come up more often in Ramona than anywhere else we work, and for good reason.",
    solar:
      "Grid reliability is the driver here. Long rural feeders and public safety power shutoffs mean most Ramona customers want storage sized to run a well pump and a refrigerator for days, not just to shave a bill.",
    permits:
      "Southern California permits with county fire review. Rural addresses sometimes add an access or defensible-space condition, which we sort out before work starts.",
    nearby: ["rancho-san-diego", "lakeside", "el-cajon", "spring-valley"],
  },
  {
    slug: "mission-valley",
    name: "Mission Valley, Southern California",
    shortName: "Mission Valley",
    zone: "urban",
    neighborhoods: ["Civita", "Grantville", "Fashion Valley side", "Friars Rd corridor", "Serra Mesa edge"],
    landmarks: ["Friars Rd", "Fashion Valley", "the Southern California River", "Snapdragon Stadium"],
    housing:
      "Mission Valley is condos, townhomes, mid-rise buildings and mixed-use property more than single-family homes. That means low-slope and flat roof systems, shared roof decks, HOA boards and rooftop mechanical equipment to work around.",
    climate:
      "The valley traps heat and holds the marine layer in the mornings. On flat roofs, standing water at low spots and around drains is the number one issue we get called about, and ponding is what shortens a membrane's life here.",
    solar:
      "Shared roofs and HOA-controlled common areas change how solar happens in Mission Valley. Projects are usually building-level rather than unit-level, and metering has to be sorted out with the association before design.",
    permits:
      "City of Southern California permitting plus HOA and property management approval. Access, parking and staging need scheduling in advance on most buildings here.",
    nearby: ["san-diego", "la-mesa", "lemon-grove", "santee"],
  },
  ...batch1Cities,
  ...batch2Cities,
  ...batch3Cities,
  ...batch4Cities,
];

export const caCityLocal: Record<string, CaCityLocal> = Object.fromEntries(
  cities.map((c) => [c.slug, c]),
);

export const caCityLocalList = cities;
